import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  CheckCircle, 
  XCircle, 
  Loader2, 
  Link2, 
  Unlink,
  AlertTriangle
} from 'lucide-react';
import { apiFetch } from '../utils/api';
import { useI18n } from '../hooks/useI18n';

interface OAuthConnection {
  id: string;
  organization_id: string;
  google_email: string | null;
  is_active: boolean;
  created_at: string;
  last_used_at: string | null;
}

interface GoogleDriveConnectProps {
  costCenterId?: string;
  returnTo?: string;
  onConnectionChange?: (connected: boolean) => void;
  compact?: boolean;
}

export default function GoogleDriveConnect({ 
  costCenterId, 
  returnTo,
  onConnectionChange,
  compact = false
}: GoogleDriveConnectProps) {
  const { t } = useI18n();
  const [connection, setConnection] = useState<OAuthConnection | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check for OAuth callback results in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gdriveConnected = params.get('gdrive_connected');
    const gdriveError = params.get('gdrive_error');

    if (gdriveConnected === 'true') {
      // Clean up URL
      const url = new URL(window.location.href);
      url.searchParams.delete('gdrive_connected');
      window.history.replaceState({}, '', url.toString());
      
      // Reload connection status
      loadConnection();
    }

    if (gdriveError) {
      setError(getErrorMessage(gdriveError));
      
      // Clean up URL
      const url = new URL(window.location.href);
      url.searchParams.delete('gdrive_error');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  const getErrorMessage = (errorCode: string): string => {
    switch (errorCode) {
      case 'access_denied':
        return 'Access was denied. Please try again and grant the required permissions.';
      case 'invalid_state':
        return 'Invalid or expired authorization session. Please try again.';
      case 'token_exchange_failed':
        return 'Failed to complete authorization. Please try again.';
      case 'no_refresh_token':
        return 'Failed to get offline access. Please try again and ensure you grant all permissions.';
      case 'internal_error':
        return 'An unexpected error occurred. Please try again.';
      default:
        return `Authorization failed: ${errorCode}`;
    }
  };

  const loadConnection = async () => {
    try {
      setLoading(true);
      const response = await apiFetch('/api/gdrive-oauth/connection');
      
      if (response.ok) {
        const data = await response.json();
        setConnection(data);
        onConnectionChange?.(!!data);
      } else if (response.status === 404 || response.status === 204) {
        setConnection(null);
        onConnectionChange?.(false);
      } else {
        console.error('Failed to load connection');
      }
    } catch (err) {
      console.error('Error loading connection:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConnection();
  }, []);

  const handleConnect = async () => {
    try {
      setConnecting(true);
      setError(null);

      const payload: any = {};
      if (costCenterId) {
        payload.cost_center_id = costCenterId;
      }
      if (returnTo) {
        payload.return_to = returnTo;
      } else {
        // Default to current page
        payload.return_to = window.location.pathname + window.location.search;
      }

      const response = await apiFetch('/api/gdrive-oauth/start', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to Google OAuth consent page
        window.location.href = data.auth_url;
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to start authorization');
      }
    } catch (err) {
      console.error('Error starting OAuth:', err);
      setError('Failed to start authorization');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect your Google account? This will disable Google Drive sync for all cost centers.')) {
      return;
    }

    try {
      setDisconnecting(true);
      setError(null);

      const response = await apiFetch('/api/gdrive-oauth/revoke', {
        method: 'POST',
      });

      if (response.ok) {
        setConnection(null);
        onConnectionChange?.(false);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to disconnect');
      }
    } catch (err) {
      console.error('Error disconnecting:', err);
      setError('Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">{t('gdrive.loadingConnection', 'common')}</span>
      </div>
    );
  }

  if (compact) {
    // Compact view for embedding in forms
    return (
      <div className="space-y-2">
        {error && (
          <Alert variant="destructive" className="py-2">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="text-sm">{error}</AlertDescription>
          </Alert>
        )}
        
        {connection ? (
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-sm">
                {t('gdrive.connectedAs', 'common')} <strong>{connection.google_email || 'Google Account'}</strong>
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDisconnect}
              disabled={disconnecting}
              className="text-red-600 hover:text-red-700"
            >
              {disconnecting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Unlink className="h-4 w-4" />
              )}
            </Button>
          </div>
        ) : (
          <Button
            onClick={handleConnect}
            disabled={connecting}
            variant="outline"
            size="sm"
          >
            {connecting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Link2 className="h-4 w-4 mr-2" />
            )}
            {t('gdrive.connectAccount', 'common')}
          </Button>
        )}
      </div>
    );
  }

  // Full card view
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {connection ? (
            <CheckCircle className="h-5 w-5 text-green-600" />
          ) : (
            <XCircle className="h-5 w-5 text-gray-400" />
          )}
          {t('gdrive.connectionTitle', 'common')}
        </CardTitle>
        <CardDescription>
          {connection 
            ? t('gdrive.connectionActive', 'common')
            : t('gdrive.connectionInactive', 'common')
          }
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {connection ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('gdrive.connectedAccount', 'common')}</p>
                <p className="font-medium">{connection.google_email || 'Unknown account'}</p>
              </div>
              <Badge variant="default" className="bg-green-600">
                {t('status.connected', 'common')}
              </Badge>
            </div>

            {connection.last_used_at && (
              <p className="text-xs text-muted-foreground">
                {t('gdrive.lastUsed', 'common')}: {new Date(connection.last_used_at).toLocaleString()}
              </p>
            )}

            <Button
              variant="destructive"
              onClick={handleDisconnect}
              disabled={disconnecting}
            >
              {disconnecting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Unlink className="h-4 w-4 mr-2" />
              )}
              {t('gdrive.disconnect', 'common')}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('gdrive.connectDescription', 'common')}
            </p>
            
            <Button
              onClick={handleConnect}
              disabled={connecting}
            >
              {connecting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Link2 className="h-4 w-4 mr-2" />
              )}
              {t('gdrive.connectWithGoogle', 'common')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
